/**
 * Composable for handling travel selection in the rebooking process
 */
import { computed } from 'vue'
import { useBookingStore } from '@/stores/bookingStore'
import { useMyTravelStore } from '@/stores/myTravelStore'
import { normalizePrice } from '@/utils/priceNormalization'
import type { Travel } from '@/types/travelTypes'
import type { Ticket } from '@/types/myTravelTypes'

export function useTravelSelection() {
  const bookingStore = useBookingStore()
  const myTravelStore = useMyTravelStore()
  
  const bookingPassengers = computed(() => bookingStore.bookingData?.passengers || [])
  
  /**
   * Normalize ticket price
   */
  const normalizeTicketPrice = (price: number | null | undefined) => {
    return normalizePrice(price || 0)
  }
  
  /**
   * Find product family for a tariff code
   */
  const findProductFamily = (tariffCode: string, direction: 'outbound' | 'inbound') => {
    const segments = direction === 'outbound' 
      ? bookingStore.bookingData?.outbound_booking_tariff_segments 
      : bookingStore.bookingData?.inbound_booking_tariff_segments
      
    const product = segments?.[0]?.required_products?.find(
      (p) => p.tariff_code === tariffCode
    )
    
    return product?.product_family_id || ''
  }
  
  /**
   * Process selected travel data and update stores
   */
  const processSelectedTravel = (travel: Travel, direction: 'outbound' | 'inbound') => {
    // Use the routeIndex property if available, otherwise default to 0
    const routeIndex = 'routeIndex' in travel ? travel.routeIndex : 0
    console.log(`[DEBUG] Processing ${direction} travel with routeIndex: ${routeIndex}`)
    
    const selectedRoute = travel.routes?.[routeIndex]
    if (!selectedRoute) {
      console.warn(`[WARN] No route found at index ${routeIndex} for ${direction} travel`)
      return
    }
    
    // Check if the selected route has bundles
    if (!Array.isArray(selectedRoute.bundles) || selectedRoute.bundles.length === 0) {
      console.warn(`[WARN] No bundles found in selected route at index ${routeIndex} for ${direction} travel`)
      return
    }
    
    const selectedBundle = selectedRoute.bundles[0]
    if (!selectedBundle?.items?.length) {
      console.warn(`[WARN] No items found in selected bundle for ${direction} travel`)
      return
    }
    
    // Check if we're in rebooking mode
    const isRebooking = myTravelStore.isRebooking
    
    // Create tickets from bundle items
    const tickets = selectedBundle.items.map(
      (item): Ticket => {
        // In rebooking mode, use the bundle's total_price_to_be_paid instead of the item price
        // This ensures we use the rebooking fee (e.g., 39 NOK) instead of the full ticket price
        let ticketPrice = item.price
        
        if (isRebooking && selectedBundle.total_price_to_be_paid !== undefined) {
          // For the required ticket (product_type === 'ST'), use the bundle's total_price_to_be_paid
          if (item.product_type === 'ST') {
            console.log(`[DEBUG] Using bundle total_price_to_be_paid (${selectedBundle.total_price_to_be_paid}) instead of item price (${item.price}) for rebooking`)
            ticketPrice = selectedBundle.total_price_to_be_paid
          }
        }
        
        return {
          id: item.id,
          passenger_id: item.passenger_fares?.[0]?.passenger_id || '',
          passenger_type:
            bookingPassengers.value.find(
              (p) => p.id === item.passenger_fares?.[0]?.passenger_id
            )?.type || 'adult',
          price: normalizeTicketPrice(ticketPrice),
          total_price_to_be_paid: normalizeTicketPrice(ticketPrice),
          product_family: item.tariff_code,
          product_family_id: findProductFamily(item.tariff_code, direction),
          tariff_code: item.tariff_code,
          passenger_fare: item.passenger_fares?.[0],
          passenger_fares: item.passenger_fares || [],
          product_code: item.product_code,
          seat: item.seat,
          departure_date_time: selectedRoute.legs?.[0]?.departure_date_time,
          arrival_date_time: selectedRoute.legs?.[0]?.arrival_date_time,
          origin_station: selectedRoute.legs?.[0]?.departure_station,
          destination_station: selectedRoute.legs?.[0]?.arrival_station,
        }
      }
    )
    
    // Update passenger counts and selected tickets
    myTravelStore.updatePassengerCounts(
      direction,
      bookingPassengers.value,
      tickets
    )
    
    myTravelStore.updateSelectedTickets(direction, tickets)
    
    // Set travel data in store
    if (direction === 'outbound') {
      myTravelStore.setOutboundTravel({
        ...travel,
        direction,
        routes: [selectedRoute],
        bundles: [selectedBundle],
      })
    } else {
      myTravelStore.setInboundTravel({
        ...travel,
        direction,
        routes: [selectedRoute],
        bundles: [selectedBundle],
      })
    }
  }
  
  return {
    processSelectedTravel,
    bookingPassengers,
    normalizeTicketPrice
  }
}
